import { STREET_ALIASES, isStreetAlias } from "./street-aliases.js";
import type { CanonicalAddress } from "./types.js";

const addressNumberPattern = /^\d+[a-z]?$/u;
const postcodePattern = /^\d{4}$/u;

export const UNIT_MARKERS: ReadonlySet<string> = new Set([
  "unit",
  "flat",
  "apartment",
  "apt",
  "suite",
  "room",
  "rm",
  "villa"
]);

export type ParsedAddressNumberToken = {
  token: string;
  number: string;
  suffix: string | null;
};

export type ParsedAddressRange = {
  token: string;
  low: ParsedAddressNumberToken;
  high: ParsedAddressNumberToken;
};

export type ParsedAddressQuery = {
  raw: string;
  normalised: string;
  tokens: string[];
  rawTokens: string[];
  unitValue: string | null;
  hasUnitIntent: boolean;
  addressNumber: ParsedAddressNumberToken | null;
  range: ParsedAddressRange | null;
  postcode: string | null;
  postcodeTokens: string[];
};

export function normaliseWhitespace(input: string): string {
  return input.replace(/\s+/g, " ").trim();
}

export function removePunctuation(input: string): string {
  return normaliseWhitespace(input.replace(/[^\p{L}\p{N}\s]+/gu, " "));
}

export function normaliseMacrons(input: string): string {
  return input.normalize("NFD").replace(/\p{M}+/gu, "");
}

export function deriveAsciiLabel(input: string): string {
  return normaliseWhitespace(normaliseMacrons(input));
}

export function normaliseStreetTypes(input: string): string {
  return mapWhitespaceTokens(input, (token) => {
    const alias = token.toLowerCase();
    return isStreetAlias(alias) ? STREET_ALIASES[alias] : token;
  });
}

export function normaliseMountMt(input: string): string {
  return mapWhitespaceTokens(input, (token) => (token.toLowerCase() === "mt" ? "mount" : token));
}

export function normaliseAddressQuery(input: string): string {
  const folded = normaliseMacrons(input.toLowerCase());
  const withoutPunctuation = removePunctuation(folded);
  return normaliseWhitespace(normaliseMountMt(normaliseStreetTypes(withoutPunctuation)));
}

export function tokeniseAddressQuery(input: string): string[] {
  const normalised = normaliseAddressQuery(input);
  return normalised.length > 0 ? normalised.split(" ") : [];
}

export function tokeniseRawAddressText(input: string): string[] {
  const folded = normaliseMacrons(input.toLowerCase());
  return folded
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .split(/\s+/u)
    .filter(Boolean);
}

export function parseAddressQuery(input: string): ParsedAddressQuery {
  const tokens = tokeniseAddressQuery(input);
  const range = extractExplicitAddressRange(input);
  const likelyAddressNumber =
    range?.low ?? parseAddressNumberToken(extractLikelyAddressNumber(tokens) ?? "");
  const unitValue = extractLikelyUnitValue(input, tokens);
  const postcodeTokens = tokens.filter(isPostcodeToken);

  return {
    raw: input,
    normalised: normaliseAddressQuery(input),
    tokens,
    rawTokens: tokeniseRawAddressText(input),
    unitValue,
    hasUnitIntent: unitValue !== null || tokens.some(isUnitMarker),
    addressNumber: likelyAddressNumber,
    range,
    postcode: postcodeTokens[0] ?? null,
    postcodeTokens
  };
}

export function buildSearchText(parts: CanonicalAddress): string {
  const segments: string[] = [];
  const seen = new Set<string>();

  const addSegment = (value: string | null | undefined): void => {
    if (!hasText(value)) {
      return;
    }

    const normalised = normaliseAddressQuery(value);
    if (normalised.length === 0 || seen.has(normalised)) {
      return;
    }

    seen.add(normalised);
    segments.push(value);
  };

  addSegment(parts.components.unit?.display);
  addSegment(parts.components.level?.display);
  addSegment(addressNumberDisplay(parts));
  addSegment(roadDisplay(parts));
  addSegment(parts.components.suburb_locality);
  addSegment(parts.components.town_city);
  addSegment(parts.components.territorial_authority);
  addSegment(parts.components.postcode);
  addSegment(parts.display.postcode);

  return normaliseAddressQuery(segments.join(" "));
}

export function extractLikelyAddressNumber(tokens: string[]): string | null {
  const normalisedTokens = tokens.flatMap((token) => tokeniseAddressQuery(token));
  const candidates: Array<{ token: string; index: number }> = [];
  let skipNextNumber = false;

  for (let index = 0; index < normalisedTokens.length; index += 1) {
    const token = normalisedTokens[index];

    if (token === undefined) {
      continue;
    }

    if (isUnitMarker(token)) {
      skipNextNumber = true;
      continue;
    }

    if (!isAddressNumberToken(token)) {
      skipNextNumber = false;
      continue;
    }

    if (skipNextNumber) {
      skipNextNumber = false;
      continue;
    }

    candidates.push({ token, index });
  }

  if (candidates.length === 0) {
    return null;
  }

  const nonPostcodeCandidates = candidates.filter((candidate) => !isPostcodeToken(candidate.token));
  const first = nonPostcodeCandidates[0];
  const second = nonPostcodeCandidates[1];

  if (first !== undefined && second !== undefined && first.index === 0 && second.index === 1) {
    return second.token;
  }

  if (first !== undefined) {
    return first.token;
  }

  return null;
}

export function extractLikelyPostcode(tokens: string[]): string | null {
  const normalisedTokens = tokens.flatMap((token) => tokeniseAddressQuery(token));
  return normalisedTokens.find((token) => isPostcodeToken(token)) ?? null;
}

export function extractLikelyUnitValue(
  rawQuery: string,
  tokens = tokeniseAddressQuery(rawQuery)
): string | null {
  const slashMatch = normaliseMacrons(rawQuery.toLowerCase()).match(
    /\b([a-z0-9]+)\s*\/\s*\d+[a-z]?\b/u
  );
  const slashUnit = slashMatch?.[1];

  if (slashUnit !== undefined) {
    return normaliseAddressQuery(slashUnit);
  }

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];

    if (token !== undefined && isUnitMarker(token)) {
      return tokens[index + 1] ?? null;
    }
  }

  const compactUnit = tokens.find((token) => /^unit[a-z0-9]+$/u.test(token));
  return compactUnit === undefined ? null : compactUnit.slice("unit".length);
}

export function extractExplicitAddressRange(input: string): ParsedAddressRange | null {
  const folded = normaliseMacrons(input.toLowerCase());
  const match = /\b(\d+[a-z]?)\s*-\s*(\d+[a-z]?)\b/u.exec(folded);

  if (match === null) {
    return null;
  }

  const low = parseAddressNumberToken(match[1] ?? "");
  const high = parseAddressNumberToken(match[2] ?? "");

  if (low === null || high === null) {
    return null;
  }

  return {
    token: `${low.token}-${high.token}`,
    low,
    high
  };
}

export function parseAddressNumberToken(value: string): ParsedAddressNumberToken | null {
  const normalised = normaliseAddressQuery(value);
  const match = /^(\d+)([a-z])?$/u.exec(normalised);

  if (match === null) {
    return null;
  }

  const [, number, suffix] = match;

  if (number === undefined) {
    return null;
  }

  return {
    token: normalised,
    number,
    suffix: suffix ?? null
  };
}

export function isUnitMarker(token: string): boolean {
  return UNIT_MARKERS.has(token);
}

export function isAddressNumberToken(token: string): boolean {
  return addressNumberPattern.test(token);
}

export function isPostcodeToken(token: string): boolean {
  return postcodePattern.test(token);
}

function mapWhitespaceTokens(input: string, mapToken: (token: string) => string): string {
  const normalised = normaliseWhitespace(input);

  if (normalised.length === 0) {
    return "";
  }

  return normalised.split(" ").map(mapToken).join(" ");
}

function addressNumberDisplay(parts: CanonicalAddress): string {
  if (hasText(parts.components.address_number.display)) {
    return parts.components.address_number.display;
  }

  const { prefix, number, suffix } = parts.components.address_number;
  return `${prefix ?? ""}${number}${suffix ?? ""}`;
}

function roadDisplay(parts: CanonicalAddress): string {
  if (hasText(parts.components.road.display)) {
    return parts.components.road.display;
  }

  return [
    parts.components.road.prefix,
    parts.components.road.name,
    parts.components.road.type,
    parts.components.road.suffix
  ]
    .filter(hasText)
    .join(" ");
}

function hasText(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
